import React, { useState } from "react";
import { X } from "lucide-react";
import axios from "../../routers/axios";
import { Editor } from "@tinymce/tinymce-react";
import { motion, AnimatePresence } from "framer-motion";

const CreateReadingWritingModal = ({ isOpen, onClose, type = "reading", onCreated }) => {
    const [formData, setFormData] = useState({
        level: "B1",
        writing_type: "essay",
    });

    const handleEditorChange = (field) => (content) => {
        setFormData((prev) => ({ ...prev, [field]: content }));
    };

    const handleInputChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async () => {
        try {
            const cleanPayload = Object.fromEntries(
                Object.entries(formData).map(([k, v]) => {
                    if (v === "" || v === null || v === undefined) return [k, null];
                    return [k, v];
                })
            );

            // ⚙️ Nếu là Reading, loại bỏ 2 field không hợp lệ
            if (type === "reading") {
                delete cleanPayload.level;
                delete cleanPayload.writing_type;
            }

            console.log("[CreateReadingWritingModal] Payload gửi đi:", cleanPayload);

            await axios.post(`/scenarios/${type}/create`, cleanPayload);
            alert(`✅ Đã tạo mới ${type === "reading" ? "Reading" : "Writing"} thành công!`);
            onCreated && onCreated();
            onClose();
        } catch (err) {
            console.error("❌ [CreateReadingWritingModal] Lỗi tạo:", err);
            alert("Lỗi khi tạo mới! Xem console để biết thêm chi tiết.");
        }
    };


    const baseEditorConfig = {
        height: 300,
        menubar: true,
        plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "preview",
            "anchor",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "help",
            "wordcount",
        ],
        toolbar:
            "undo redo | blocks | bold italic underline forecolor | alignleft aligncenter alignright alignjustify | " +
            "bullist numlist outdent indent | link image media table | code fullscreen | help",
        content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
        paste_data_images: true,
    };

    const renderEditor = (label, field, placeholder) => (
        <div className="mb-5">
            <label className="block text-sm font-semibold mb-2">{label}</label>
            <Editor
                apiKey="a7bzgpdrc4fmam222696wcyaaybnyn3vmqk9wk86wamc80as"
                init={{ ...baseEditorConfig, placeholder }}
                value={formData[field]}
                onEditorChange={handleEditorChange(field)}
            />
        </div>
    );

    const renderInput = (label, field, type = "text", placeholder) => (
        <div className="mb-5">
            <label className="block text-sm font-semibold mb-2">{label}</label>
            <input
                type={type}
                className="w-full border rounded-lg p-2"
                placeholder={placeholder}
                value={formData[field] || ""}
                onChange={handleInputChange(field)}
            />
        </div>
    );

    const renderLevelSelector = () => {
        const levels = ["A1", "A2", "B1", "B2", "C1", "C2"];
        return (
            <div className="mb-5">
                <label className="block text-sm font-semibold mb-2">Trình độ (Level)</label>
                <div className="flex flex-wrap gap-4">
                    {levels.map((lvl) => (
                        <label
                            key={lvl}
                            className={`flex items-center gap-2 border px-3 py-2 rounded-lg cursor-pointer ${formData.level === lvl ? "bg-blue-600 text-white" : "hover:bg-gray-100"
                                }`}
                        >
                            <input
                                type="radio"
                                name="level"
                                value={lvl}
                                checked={formData.level === lvl}
                                onChange={(e) => setFormData((p) => ({ ...p, level: e.target.value }))}
                                className="hidden"
                            />
                            {lvl}
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="bg-white w-[90vw] max-w-6xl rounded-xl shadow-2xl overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-lg font-bold">
                                Tạo mới {type === "reading" ? "Reading" : "Writing"}
                            </h2>
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                            {type === "reading" ? (
                                <>
                                    {renderEditor("Reading Title", "reading_title", "Nhập tiêu đề Reading")}
                                    {renderEditor("Reading Description", "reading_description")}
                                    {renderInput("Reading Duration", "reading_duration")}
                                    {renderInput("Reading Video (URL)", "reading_video")}
                                    {renderEditor("Reading Instruction", "reading_instruction")}
                                    {renderInput("Passage Title", "passage_title")}
                                    {renderEditor("Passage Content", "passage_content")}
                                    {renderEditor("Passage Reference", "passage_reference")}
                                    {renderEditor("Passage Notes", "passage_notes")}
                                    {renderEditor("Marking Rubric", "marking_rubric")}
                                    {renderInput("Video Filename", "video_filename")}
                                </>
                            ) : (
                                <>
                                    {renderEditor("Writing Title", "writing_title")}
                                    {renderLevelSelector()}
                                    {renderInput("Estimated Time (minutes)", "estimated_time_minutes", "number")}
                                    {renderEditor("Instruction Intro", "instruction_intro")}
                                    {renderEditor("Instruction Additional", "instruction_additional")}
                                    {renderInput("Instruction Image (URL)", "instruction_image")}
                                    {renderInput("Instruction Video (URL)", "instruction_video")}
                                    {renderInput("Message Title", "message_title")}
                                    {renderInput("Message Sender", "message_sender")}
                                    {renderEditor("Message Text", "message_text")}
                                    {renderInput("Message Image (URL)", "message_image")}
                                    {renderEditor("Guidelines Title", "guidelines_title")}
                                    {renderEditor("Guidelines Description", "guidelines_description")}
                                    {renderEditor("Rubric Description", "rubric_description")}
                                    {renderEditor("Outline Instruction", "outline_instruction")}
                                    {renderEditor("Outline Placeholder", "outline_placeholder")}
                                    {renderEditor("Writing Instruction", "writing_instruction")}
                                    {renderEditor("Writing Placeholder", "writing_placeholder")}
                                    {renderEditor("Submission Note", "submission_note")}
                                    {renderInput("Max Word Count", "max_word_count", "number")}
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
                            <button onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-gray-100">
                                Hủy
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                            >
                                Lưu
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CreateReadingWritingModal;